import { useQuery } from "@tanstack/react-query";
import { seminarApi } from "../../services/endpoints";
import { unwrapList } from "../../services/response";
import { provinceToStateId } from "../../components/shared/listofGermanStates";
import { formatTime, formatDateGerman, todayISODate } from "../../utils/dateUtils";
import { PARTICIPANT_STATUS } from "../../constants/participants";
import type { Seminar } from "../../types/entities";

/**
 * The Strapi filter params a seminar query is pointed at (or null). Exported so
 * the seminar store can drop its local re-cast of this hook's signature (the
 * store builds params of exactly this shape).
 */
export type SeminarQueryParams = Record<string, string | number | Date>;

export const seminarsQueryKey = ["seminars"];

// Flatten + decorate a raw Strapi seminar (moved verbatim from the old getData).
const transformSeminar = (seminar: Seminar): Seminar => {
  // The list query always populates `raum`, so it is an object or absent here;
  // the typeof guard narrows away the unpopulated-id (number) case entities.ts
  // still models, letting us read raum.bundesland without a cast.
  const rawRaum = seminar.raum;
  const raum =
    rawRaum && typeof rawRaum === "object"
      ? {
          ...rawRaum,
          bundesland_id: provinceToStateId(rawRaum.bundesland),
        }
      : null;

  const referents = (seminar.vb_sem_refs || [])
    .map((vbRef) => {
      if (!vbRef) return null;
      const referentData = vbRef.id_referent;
      // Junction always populates id_referent (an object); the typeof guard
      // narrows away the unpopulated-id (number) case so the spread below yields
      // a Referent — carrying the display fields the query asks for (see the
      // `fields` list below), not the whole referent record.
      if (!referentData || typeof referentData !== "object") return null;
      return {
        vbSemRefId: vbRef.documentId,
        ...referentData,
        ref_brutto_honorar_euro: vbRef.ref_brutto_honorar_euro,
        ist_mehrwert_steuer: vbRef.ist_mehrwert_steuer,
        zahlung_methode: vbRef.zahlung_methode,
      };
    })
    .filter((ref): ref is NonNullable<typeof ref> => ref !== null);

  return {
    ...seminar,
    // The date/time columns are optional on the entity; these formatters return
    // "" for falsy input, so `?? ""` is behaviour-identical to the old JS.
    datum_friendly_adjusted: formatDateGerman(seminar.datum ?? ""),
    end_datum_friendly_adjusted: formatDateGerman(seminar.end_datum ?? ""),
    anfangs_zeit_friendly_adjusted: formatTime(seminar.anfangs_zeit ?? ""),
    end_zeit_friendly_adjusted: formatTime(seminar.end_zeit ?? ""),
    raum,
    referent: referents,
  };
};

/**
 * Query options for the seminar list. staleTime:0 keeps it always-fresh — the
 * seminar list is actively edited, so it always reloads (identical to the
 * previous hand-rolled behaviour); the query layer adds request dedup and routes
 * the fetch through the shared client. The queryKey is scoped by the active
 * filter (['seminars', query ?? null]) so switching filters switches the fetch.
 * Selection lives in the seminar store (selectedSeminarId); the on-screen list
 * is derived from this query's cache for the active filter.
 */
export const seminarsQueryOptions = (query: SeminarQueryParams | null = null) => ({
  queryKey: [...seminarsQueryKey, query ?? null],
  staleTime: 0,
  queryFn: async (): Promise<Seminar[]> => {
    const res = await seminarApi.getAll({
      // No filter selected (null — historically also `false`): default to the
      // upcoming-seminars view. `!query` covers both.
      ...(!query && {
        "filters[$or][0][datum][$gte]": todayISODate(),
        "filters[$or][1][end_datum][$gte]": todayISODate(),
        "sort[0]": "datum",
      }),
      ...(query && query),
      // The populate set is deliberately narrow: this is the app's hottest query
      // (staleTime:0, and it backs the seminar list, the invoice + email views and
      // every detail panel), so each relation goes down to the fields actually
      // read. Strapi 5 always returns id + documentId on a populated entry, so a
      // `fields` list never costs us identity.
      //   - id_referent: the 4 display fields (grid/CSV "Referent/-in" column,
      //     ReferentTab row, email templates). The per-seminar honorar/MwSt live on
      //     the junction row itself, not on the referent, so they are unaffected.
      //   - amt: an invoice's amt is only ever read for its id (the grid's company
      //     filter); a participant's amt for `bezeichnung` (the "Kunde" column,
      //     hoisted to clientData by ParticipantsTab) and for its id (numberPerson
      //     in useInvoiceDerived, and the client-directory swap pin).
      //   - rechnung_id: `status` is the only field read off a participant's
      //     invoice (the participants grid's delete gate — an invoiced row is only
      //     deletable once bezahlt); every other reader matches on identity alone
      //     (invoiceParticipantConnection, and invoiceStore's manual-invoice
      //     participant auto-select), which id + documentId already cover. Without
      //     the `fields` list this dragged a whole ~38-scalar Rechnung into every
      //     participant row.
      // Two relations are deliberately NOT populated: sem_tn_listes.id_seminar
      // (the back-pointer to the seminar we already have — it nested a full parent
      // copy into every participant row, with zero readers) and invoices.seminar
      // (same shape; the only reader was a gate that compared it against the very
      // seminar whose invoices these are — see domain/invoiceRules.hasOffenInvoice).
      "populate[vb_sem_refs][populate][id_referent][fields][0]": "name",
      "populate[vb_sem_refs][populate][id_referent][fields][1]": "vorname",
      "populate[vb_sem_refs][populate][id_referent][fields][2]": "titel",
      "populate[vb_sem_refs][populate][id_referent][fields][3]": "anrede",
      "populate[raum]": true,
      "populate[sem_tn_listes][populate][id_teilnehmer][populate][amt][fields][0]": "bezeichnung",
      "populate[sem_tn_listes][populate][rechnung_id][fields][0]": "status",
      "populate[sem_tn_listes][filters][status]": PARTICIPANT_STATUS.ACTIVE,
      "populate[invoices][populate][amt][fields][0]": "id",
    });
    const data = unwrapList<Seminar>(res);
    return data.map(transformSeminar);
  },
});

/**
 * The on-screen seminar list for the active filter. A REAL query now: it fetches
 * automatically for the given filter (key ['seminars', query ?? null]) and, with
 * staleTime:0, refetches whenever it's driven — so an invalidate/refetch of the
 * active key updates the UI without any imperative setQueryData. Selection lives
 * in the seminar store (selectedSeminarId); useSeminarInputs resolves the
 * selected row from this list.
 */
export const useSeminarSelection = (query: SeminarQueryParams | null) =>
  useQuery({ ...seminarsQueryOptions(query), staleTime: 0 }).data ?? [];
